import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Spin, Tag, message } from 'antd';
import { CustomerServiceOutlined, CloseOutlined, SendOutlined, RobotOutlined } from '@ant-design/icons';
import './index.css';

const QUICK_QUESTIONS = [
  'PAI 和 PMS 的定位区别是什么？',
  '如何在 WorkOrder 里快速筛选待办订单？',
  'NO CARGO RELEASE 和 DEMURRAGE 的提醒逻辑是什么？',
  '订单详情页里 Attachments / Emails / A/N 分别怎么用？',
];

const DASHSCOPE_BASE_URL = 'https://dashscope.aliyuncs.com/compatible-mode/v1';
const DASHSCOPE_MODEL = 'deepseek-v4-flash';
const DASHSCOPE_API_KEY = 'sk-ab9c98269a974e3ea133b752846124d4';

const SOPHIA_SYSTEM_PROMPT = `
你是 Prime 内部系统 PAI 的客服 Sophia。回答必须简洁、准确、可执行，优先用步骤化说明。
【系统价值】
PAI 逐步替代 PMS，目标是提升操作效率和自动化：提醒 OP 待办、自动发送 AN/PU、自动收集邮件和 House 文件到订单。PAI 技术架构更新、云端部署、扩展性更好。因财务报表能力暂不完整，PMS 仍是主平台，PAI 目前为辅助。
【核心操作】
1) 新建订单：入口在 WorkOrder/Create，但当前订单主数据仍以 PMS 为准，新建仍应在 PMS 或代理上传 H 单触发。
2) 筛选订单：支持字段筛选（如 NO A/N Date、NO Release Date、Prime Note=ZZ）和进度/异常看板筛选；这两类筛选不能同时生效。
3) 编辑订单：右侧按钮进详情页；可用模块包括 Edit、Attachments、Emails、A/N、H 单预览、叹号字段比对提示。
4) 提醒中心：基于字段缺失、日期比较、规则触发提醒/待办（NO MAN、NO A/N、NO CARGO RELEASE、DEMURRAGE、DETENTION、NO EMPTY RETURN、ZZ、AI PARSE 等）。
5) 记录中心：A/N 与 PU 发送记录可追踪，失败可重发，需定期检查失败记录。
【回答规则】
- 不确定时明确说“需到订单详情/提醒中心核对字段”。
- 涉及提醒时，明确触发条件+时间阈值+对象（客户/OP）。
- 不要编造系统中不存在的页面和字段。
`;

function SophiaAssistant() {
  const [messageApi, contextHolder] = message.useMessage();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', content: '你好，我是 PAI 内部客服 Sophia。你可以先点右侧快捷问题。' },
  ]);
  const messagesEndRef = useRef(null);

  const canSend = useMemo(() => input.trim().length > 0 && !loading, [input, loading]);

  const requestAI = async (question) => {
    const userMsg = { role: 'user', content: question };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setLoading(true);
    try {
      if (!DASHSCOPE_API_KEY) {
        throw new Error('Missing REACT_APP_DASHSCOPE_API_KEY');
      }

      const res = await fetch(`${DASHSCOPE_BASE_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${DASHSCOPE_API_KEY}`,
        },
        body: JSON.stringify({
        model: DASHSCOPE_MODEL,
        messages: [
          { role: 'system', content: SOPHIA_SYSTEM_PROMPT },
          ...nextMessages.map((m) => ({ role: m.role, content: m.content })),
        ],
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errText}`);
      }

      const data = await res.json();

      const answer = data?.data?.content
        || data?.content
        || data?.choices?.[0]?.message?.content
        || '收到你的问题了，但我暂时没有拿到有效回复，请稍后重试。';

      setMessages((prev) => [...prev, { role: 'assistant', content: answer }]);
    } catch (error) {
      messageApi.error(`Sophia 服务暂时不可用：${error?.response?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    await requestAI(q);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading]);

  return (
    <div className="sophia-widget">
      {contextHolder}

      {open && (
        <div className="sophia-panel">
          <div className="sophia-header">
            <div className="sophia-title"><CustomerServiceOutlined /> PAI Internal Support - Sophia</div>
            <Button type="text" icon={<CloseOutlined />} onClick={() => setOpen(false)} />
          </div>

          <div className="sophia-main">
            <div className="sophia-left">
              <div className="sophia-messages">
                {messages.map((m, idx) => (
                  <div key={`${m.role}-${idx}`} className={`sophia-msg sophia-${m.role}`}>
                    {m.content}
                  </div>
                ))}
                {loading && (
                  <div className="sophia-loading"><Spin size="small" /> Sophia is thinking...</div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="sophia-input">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onPressEnter={handleSend}
                  placeholder="Ask Sophia..."
                />
                <Button type="primary" icon={<SendOutlined />} onClick={handleSend} disabled={!canSend} />
              </div>
            </div>

            <div className="sophia-right">
              <div className="sophia-intro">
                <div className="sophia-intro-title">Sophia 简介</div>
                <div className="sophia-intro-text">
                  PAI 内部客服，帮助你快速理解 WorkOrder、提醒中心、A/N 与 PU 自动化规则。
                </div>
              </div>

              <div className="sophia-quick">
                {QUICK_QUESTIONS.map((q) => (
                  <Tag
                    key={q}
                    className={`sophia-quick-tag${loading ? ' disabled' : ''}`}
                    onClick={() => !loading && requestAI(q)}
                  >
                    {q}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!open && (
        <Button
          type="primary"
          shape="circle"
          size="large"
          className="sophia-fab"
          icon={<RobotOutlined />}
          onClick={() => setOpen(true)}
        />
      )}
    </div>
  );
}

export default SophiaAssistant;
